import React, { useState } from 'react';
import { TableBody, TableCell, TableHead, TableRow, Table, TableContainer, useMediaQuery, IconButton, Checkbox } from '@mui/material';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { formatFirestoreTimestampMMDDYYYY } from '../../../utils/time_utils/TimeUtils';
import { useNavigate } from 'react-router-dom';
import { IconCaretRightFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import CustomPagination from '../../../components/pagination/CustomPagination';

const AssetTable = ({ assets, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, onSelectionChange, isSelectionMode }) => {
  const { organizationEmployees } = useOrganizationContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { t } = useTranslation();
  const [selectedAssets, setSelectedAssets] = useState([]);
  
  const handleRowClick = (assetId) => {
    if (isSelectionMode) {
      handleSelection(assetId);
    } else {
      navigate(`/assets/${assetId}`);
    }
  };

  const handleSelection = (assetId) => {
    const newSelectedAssets = selectedAssets.includes(assetId)
      ? selectedAssets.filter(id => id !== assetId)
      : [...selectedAssets, assetId];
    
    setSelectedAssets(newSelectedAssets);
    onSelectionChange(newSelectedAssets);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelectedAssets = assets.map(asset => asset.id);
      setSelectedAssets(newSelectedAssets);
      onSelectionChange(newSelectedAssets);
    } else {
      setSelectedAssets([]);
      onSelectionChange([]);
    }
  };

  // Reset selection when selection mode is disabled
  React.useEffect(() => {
    if (!isSelectionMode) {
      setSelectedAssets([]);
      onSelectionChange([]);
    }
  }, [isSelectionMode, onSelectionChange]);

  return (
    <>
      <TableContainer style={{ overflowX: 'auto' }}>
        <Table aria-label="asset-table">
          <TableHead className="assets-table-header">
            <TableRow>
              {isSelectionMode && !isMobile && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedAssets.length > 0 && selectedAssets.length < assets.length}
                    checked={assets.length > 0 && selectedAssets.length === assets.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.ASSET)}</TableCell>
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.SERIAL_NUMBER)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.CONDITION)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.COST)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.PURCHASE_DATE)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.POSSESSION)}</TableCell>}
              {isMobile && <TableCell style={{ fontWeight: 'bold' }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset, index) => {
              const employee = organizationEmployees.find(emp => emp.id === asset.employee_id);
              const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : t(StringKeys.NOT_ASSIGNED);
              const isSelected = selectedAssets.includes(asset.id);

              return (
                <TableRow
                  key={index}
                  onClick={() => !isMobile && handleRowClick(asset.id)}
                  style={{ cursor: !isMobile ? 'pointer' : 'default' }}
                  selected={isSelected}
                >
                  {isSelectionMode && !isMobile && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelection(asset.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{asset.name}</TableCell>
                  {!isMobile && <TableCell>{asset.serial_number}</TableCell>}
                  {!isMobile && <TableCell>{t(asset.condition)}</TableCell>}
                  {!isMobile && <TableCell>${asset.price}</TableCell>}
                  {!isMobile && <TableCell>{formatFirestoreTimestampMMDDYYYY(asset.purchase_date)}</TableCell>}
                  {!isMobile && <TableCell>{employeeName}</TableCell>}
                  {isMobile && (
                    <TableCell sx={{textAlign:"end"}}>
                      <IconButton onClick={() => handleRowClick(asset.id)}>
                        <IconCaretRightFilled />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        rowsPerPageOptions={[10, 25, 100, { label: 'All', value: -1 }]}
        component="div"
        count={assets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage} 
      />
    </>
  );
};

export default AssetTable;